import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ProfileService } from '../../services/profile.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { usaPhoneValidator } from '../../shared/validators/phone.validator';
import { strictEmailValidator } from '../../shared/validators/email.validator';
import { standardNameValidator } from '../../shared/validators/name.validator';

/**
 * BusinessProfileComponent manages viewing and editing a business profile (name, email, phone) and its logo; it provides form validation, image upload/removal, change tracking, and update submission.
 *
 * Purpose:
 *   - Loads the current business profile on init, populates a reactive form, and displays a logo or generated initials.
 *   - Allows switching to edit mode, selecting/removing a logo image (with validation and preview), tracking unsaved changes, and submitting updates to the server.
 *
 * Key methods:
 *   - calculateInitials(): Derives two-letter initials from the business name for avatar fallback.
 *   - onFileSelected(event): Validates image type/size, reads file as DataURL for preview, sets selectedFile and upload state, and updates change tracking.
 *   - removePhoto(): Clears the logo preview and selected file and marks the form as modified.
 *   - onSubmit(): Builds FormData (including optional logo), sends update via ProfileService, handles success/error responses, updates original state, and emits cancelClicked on success.
 *   - onEdit(): Enables edit mode and snapshots the current form and logo as originals for cancellation.
 *   - onCancel(): Restores original form values and logo, resets edit/modification state, and emits cancelClicked.
 *   - checkIfFormModified(): Compares current form fields and logo/selectedFile against stored originals to determine whether the form is modified (controls form-save enabling).
 *
 * Lifecycle hooks:
 *   - ngOnInit(): Fetches business profile via ProfileService, patches the reactive form without triggering change events, initializes original data/logo, resets modification tracking, and subscribes to businessName changes to recalculate initials.
 *
 * Important state and validation notes:
 *   - Uses a reactive FormGroup with controls: businessName (required, max 100, standardNameValidator), email (required, max 254, strictEmailValidator), phoneNumber (required, max 15, usaPhoneValidator).
 *   - Enforces image upload constraints: allowed types PNG/JPEG/JPG/GIF and max size 20MB; reports uploadError and manages isImageUploading.
 *   - Tracks flags: isEditing, isFormModified, isSubmitting, isImageUploading, selectedFile, logoUrl, initials, and stores originalFormData/originalLogoUrl for cancellation and change detection.
 *
 * @component
 * @selector app-business-profile
 * @example
 *   <app-business-profile (cancelClicked)="onProfileCancel()"></app-business-profile>
 * @input {none} none - This component does not accept external @Input properties; it loads profile data internally via ProfileService.
 * @output {EventEmitter<void>} cancelClicked - Emitted when editing is cancelled or after a successful save to notify parent components to close dialogs or refresh state.
 */
@Component({
  selector: 'app-business-profile',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './business-profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./business-profile.component.scss'],
})
export class BusinessProfileComponent implements OnInit {
  profileForm: FormGroup;
  isEditing = false;
  private initialFormValue: any;
  logoUrl: string | null = null;
  selectedFile: File | null = null;
  uploadError: string | null = null;
  isImageUploading = false;
  initials = '';
  private shopId = '';
  isFormModified = false; // Starts as false - button disabled until changes are made
  isSubmitting = false;
  @Output() cancelClicked = new EventEmitter<void>();

  // Store original data for cancellation
  private originalFormData: any = {};
  private originalLogoUrl: string | null = null;

  /**
   * Initializes the BusinessProfileComponent, creates the profile reactive form and subscribes to its changes to track modifications.
   * @example
   * new BusinessProfileComponent(fb, profileService, messageService)
   * BusinessProfileComponent instance
   * @param {{FormBuilder}} {{fb}} - Angular FormBuilder used to construct the reactive FormGroup for the profile.
   * @param {{ProfileService}} {{profileService}} - Service used to retrieve and persist profile data.
   * @param {{NotificationService}} {{messageService}} - Service used to display messages to the user.
   * @returns {{void}} Constructor does not return a value.
   **/
  constructor(
    private fb: FormBuilder,
    private profileService: ProfileService,
    private messageService: NotificationService
  ) {
    this.profileForm = this.fb.group({
      businessName: ['', [Validators.required, Validators.maxLength(100), standardNameValidator()]],
      email: ['', [Validators.required, Validators.maxLength(254), strictEmailValidator()]],
      phoneNumber: ['', [Validators.required, Validators.maxLength(15), usaPhoneValidator()]],
    });
    this.initialFormValue = this.profileForm.value;

    // Track form changes
    this.profileForm.valueChanges.subscribe(() => {
      this.checkIfFormModified();
    });
  }

  /**
   * Initializes the component: loads the business profile, patches the form and original state, sets logo and flags, and subscribes to businessName changes to recalculate initials.
   * @example
   * gOnInit()
   * void
   * @returns {{void}} No return value; performs initialization and side effects.
   **/
  ngOnInit(): void {
    this.profileService.getBusinessProfile().subscribe(response => {
      if (response.status) {
        const { name, email, contact, logo, id } = response.data;
        this.shopId = id;
        const formData = {
          businessName: name,
          email: email,
          phoneNumber: contact,
        };

        // Store original data BEFORE patching to ensure accurate comparison
        this.originalFormData = {
          businessName: name,
          email: email,
          phoneNumber: contact,
        };
        this.originalLogoUrl = logo || null;

        this.profileForm.patchValue(formData, { emitEvent: false });
        this.logoUrl = logo || null;
        this.initialFormValue = this.profileForm.value;

        // Reset form modification state after loading data
        this.isFormModified = false;
        this.selectedFile = null;

        this.calculateInitials();
      }
    });

    this.businessName?.valueChanges.subscribe(() => {
      this.calculateInitials();
    });
  }

  get businessName() {
    return this.profileForm.get('businessName');
  }

  get email() {
    return this.profileForm.get('email');
  }

  get phoneNumber() {
    return this.profileForm.get('phoneNumber');
  }

  /**
   * Calculate and set the component initials from the current business name.
   * @example
   * calculateInitials()
   * // After calling, this.initials === 'AC' for businessName 'Acme Company' or '' if no businessName.
   * @param {void} none - No parameters.
   * @returns {void} No return value; updates this.initials on the instance.
   **/
  private calculateInitials(): void {
    const businessName = this.businessName?.value;
    if (businessName) {
      const words = businessName.trim().split(/\s+/);
      this.initials = words
        .map((word: string) => word[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    } else {
      this.initials = '';
    }
  }

  /**
   * Handle file selection from an input element: validate size/type, create a preview, and manage upload/error state.
   * @example
   * nFileSelected({ target: { files: [sampleFile] } })
   * undefined
   * @param {{any}} {{event}} - Input change event from a file input element containing the selected file.
   * @returns {{void}} No return value; updates component state (uploadError, isImageUploading, selectedFile, logoUrl) and triggers form modification check.
   **/
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.uploadError = null;
      this.isImageUploading = true;

      if (file.size > 20 * 1024 * 1024) {
        this.uploadError = 'File exceeds 20MB size limit.';
        this.isImageUploading = false;
        return;
      }

      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        this.uploadError = 'Unsupported file format. Please upload a JPG, PNG, or GIF image.';
        this.isImageUploading = false;
        event.target.value = '';
        return;
      }

      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.logoUrl = e.target.result;
        this.isImageUploading = false;

        this.checkIfFormModified();
      };
      reader.onerror = () => {
        this.uploadError = 'Error reading file. Please try again.';
        this.isImageUploading = false;
        this.selectedFile = null;
        event.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  }

  removePhoto(): void {
    this.logoUrl = null;
    this.selectedFile = null;

    this.checkIfFormModified();
  }

  /**
   * Submits the business profile form to update the server and updates local component state on success.
   * @example
   * nSubmit()
   * undefined
   * @returns {void} Performs an HTTP update request and updates component state and UI; no return value.
   **/
  onSubmit() {
    if (this.profileForm.valid && this.shopId && !this.isSubmitting) {
      this.isSubmitting = true;
      const formData = new FormData();
      formData.append('name', this.profileForm.get('businessName')?.value);
      formData.append('email', this.profileForm.get('email')?.value);
      formData.append('contact', this.profileForm.get('phoneNumber')?.value);
      if (this.selectedFile) {
        formData.append('logo', this.selectedFile, this.selectedFile.name);
      } else if (this.logoUrl === null) {
        formData.append('logo', '');
      }

      this.profileService.updateBusinessProfile(this.shopId, formData).subscribe({
        next: response => {
          if (response.status) {
            const { name, email, contact, logo } = response.data;
            const updatedFormData = {
              businessName: name,
              email: email,
              phoneNumber: contact,
            };

            // Update original data after successful save to match current state
            this.originalFormData = {
              businessName: name,
              email: email,
              phoneNumber: contact,
            };
            this.originalLogoUrl = logo || null;

            this.profileForm.patchValue(updatedFormData, { emitEvent: false });
            this.logoUrl = logo || null;
            this.initialFormValue = this.profileForm.value;

            // Reset all modification tracking
            this.isEditing = false;
            this.isFormModified = false;
            this.selectedFile = null;
            this.uploadError = null;
            this.isSubmitting = false;
            this.calculateInitials();
            this.cancelClicked.emit();
            // this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Profile updated successfully', life: 3000 });
          } else {
            // Handle error
            this.isSubmitting = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.message,
            });
          }
        },
        error: error => {
          this.isSubmitting = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update profile. Please try again.',
          });
        },
      });
    }
  }

  /**
   * Enable editing mode and back up the current profile form values and logo before allowing edits.
   * @example
   * nEdit()
   * void
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} No return value; stores the current form values and logo URL as backups and sets editing flags.
   **/
  onEdit() {
    // Store current form values and logo as backup before enabling editing
    const currentFormData = this.profileForm.value;
    this.originalFormData = {
      businessName: currentFormData.businessName,
      email: currentFormData.email,
      phoneNumber: currentFormData.phoneNumber,
    };
    this.originalLogoUrl = this.logoUrl;
    this.isEditing = true;
    this.isFormModified = false; // Reset modification tracking
  }

  onCancel() {
    // Restore original form data and logo
    this.profileForm.patchValue(this.originalFormData);
    this.logoUrl = this.originalLogoUrl;
    this.isEditing = false;
    this.isFormModified = false;
    this.selectedFile = null;
    this.uploadError = null;
    this.calculateInitials();
    this.cancelClicked.emit();
  }

  /**
   * Check if the business profile form values or selected logo have changed compared to the original values and update the isFormModified flag.
   * @example
   * this.checkIfFormModified()
   * undefined
   * @returns {void} Updates the component's isFormModified property; does not return a value.
   **/
  private checkIfFormModified() {
    // Get current form values
    const currentFormData = this.profileForm.value;
    const currentLogoUrl = this.logoUrl;

    // Compare form data fields
    const editableFields = {
      businessName: currentFormData.businessName,
      email: currentFormData.email,
      phoneNumber: currentFormData.phoneNumber,
    };

    // Check if form data has changed
    const formDataChanged =
      JSON.stringify(editableFields) !== JSON.stringify(this.originalFormData);

    // Check if logo has changed
    const logoChanged = currentLogoUrl !== this.originalLogoUrl || this.selectedFile !== null;

    this.isFormModified = formDataChanged || logoChanged;
  }
}
